/**
 * Sesiones + capturas — Analytics → Producto.
 * RPC: get_product_session_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export interface ProductSessionOverview {
    sessions: number;
    sessions_previous: number;
    users: number;
    users_previous: number;
    sessions_per_user: number | null;
    sessions_per_user_previous: number | null;
    duration_p50_sec: number | null;
    duration_p50_sec_previous: number | null;
    captures: number;
    captures_per_session: number | null;
    pct_with_capture: number | null;
}

export interface ProductSessionBucket {
    key: string;
    label: string;
    sessions: number;
    users: number;
    duration_p50_sec: number | null;
    duration_p90_sec: number | null;
    duration_avg_sec: number | null;
    captures: number;
    captures_per_session: number | null;
    sessions_with_capture: number;
    pct_with_capture: number | null;
}

export interface ProductCaptureMilestone {
    capture_n: number;
    label: string;
    users_reached: number;
    cohort_registered: number;
    reached_pct: number | null;
    hours_p50: number | null;
    hours_p90: number | null;
    session_n_p50: number | null;
    session_n_avg: number | null;
}

export interface ProductSessionAnalytics {
    period_days: number;
    comparison_label: string;
    overview: ProductSessionOverview;
    by_session: ProductSessionBucket[];
    time_to_captures: ProductCaptureMilestone[];
}

function asNumber(v: unknown, fallback = 0): number {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}

function asNullableNumber(v: unknown): number | null {
    if (v == null) return null;
    const n = asNumber(v, Number.NaN);
    return Number.isFinite(n) ? n : null;
}

function parseOverview(raw: unknown): ProductSessionOverview {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        sessions: asNumber(o.sessions),
        sessions_previous: asNumber(o.sessions_previous),
        users: asNumber(o.users),
        users_previous: asNumber(o.users_previous),
        sessions_per_user: asNullableNumber(o.sessions_per_user),
        sessions_per_user_previous: asNullableNumber(o.sessions_per_user_previous),
        duration_p50_sec: asNullableNumber(o.duration_p50_sec),
        duration_p50_sec_previous: asNullableNumber(o.duration_p50_sec_previous),
        captures: asNumber(o.captures),
        captures_per_session: asNullableNumber(o.captures_per_session),
        pct_with_capture: asNullableNumber(o.pct_with_capture),
    };
}

function parseBucket(raw: unknown): ProductSessionBucket | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.key !== 'string') return null;
    return {
        key: o.key,
        label: typeof o.label === 'string' ? o.label : o.key,
        sessions: asNumber(o.sessions),
        users: asNumber(o.users),
        duration_p50_sec: asNullableNumber(o.duration_p50_sec),
        duration_p90_sec: asNullableNumber(o.duration_p90_sec),
        duration_avg_sec: asNullableNumber(o.duration_avg_sec),
        captures: asNumber(o.captures),
        captures_per_session: asNullableNumber(o.captures_per_session),
        sessions_with_capture: asNumber(o.sessions_with_capture),
        pct_with_capture: asNullableNumber(o.pct_with_capture),
    };
}

function parseMilestone(raw: unknown): ProductCaptureMilestone | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    const captureN = asNumber(o.capture_n, Number.NaN);
    if (!Number.isFinite(captureN)) return null;
    return {
        capture_n: captureN,
        label: typeof o.label === 'string' ? o.label : `${captureN}ª captura`,
        users_reached: asNumber(o.users_reached),
        cohort_registered: asNumber(o.cohort_registered),
        reached_pct: asNullableNumber(o.reached_pct),
        hours_p50: asNullableNumber(o.hours_p50),
        hours_p90: asNullableNumber(o.hours_p90),
        session_n_p50: asNullableNumber(o.session_n_p50),
        session_n_avg: asNullableNumber(o.session_n_avg),
    };
}

export function parseProductSessionAnalytics(raw: unknown): ProductSessionAnalytics | null {
    if (raw == null) return null;
    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (Array.isArray(payload)) payload = payload[0];
    if (!payload || typeof payload !== 'object') return null;
    const o = payload as Record<string, unknown>;
    const wrapped = o.get_product_session_analytics;
    if (wrapped && typeof wrapped === 'object') payload = wrapped;
    const p = payload as Record<string, unknown>;
    if (p.period_days == null || p.overview == null) return null;
    const bySession = Array.isArray(p.by_session)
        ? p.by_session.map(parseBucket).filter((x): x is ProductSessionBucket => x != null)
        : [];
    const milestones = Array.isArray(p.time_to_captures)
        ? p.time_to_captures.map(parseMilestone).filter((x): x is ProductCaptureMilestone => x != null)
        : [];
    return {
        period_days: asNumber(p.period_days, 30),
        comparison_label: typeof p.comparison_label === 'string'
            ? p.comparison_label
            : 'vs. periodo anterior',
        overview: parseOverview(p.overview),
        by_session: bySession,
        time_to_captures: milestones,
    };
}

export function useProductSessionAnalytics(days: number = 30) {
    return useQuery<ProductSessionAnalytics>({
        queryKey: ['admin', 'product-session-analytics', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_session_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProductSessionAnalytics(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_product_session_analytics');
            }
            return parsed;
        },
        retry: 1,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function formatDurationSec(sec: number | null): string {
    if (sec == null || !Number.isFinite(sec)) return 'n/d';
    if (sec < 60) return `${Math.round(sec)} s`;
    const minutes = sec / 60;
    if (minutes < 60) {
        return `${minutes.toLocaleString('es-ES', { maximumFractionDigits: minutes < 10 ? 1 : 0 })} min`;
    }
    const hours = Math.floor(minutes / 60);
    const rest = Math.round(minutes % 60);
    return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`;
}

export function formatHoursToHuman(hours: number | null): string {
    if (hours == null || !Number.isFinite(hours)) return 'n/d';
    if (hours < 1) {
        const mins = Math.max(1, Math.round(hours * 60));
        return `${mins} min`;
    }
    if (hours < 48) {
        return `${hours.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h`;
    }
    const days = hours / 24;
    return `${days.toLocaleString('es-ES', { maximumFractionDigits: 1 })} días`;
}
